import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {CountertopsContextType} from "../types/CountertopsContextType";
import {Countertop} from "../types/Countertop";
import {SelectedMaterial} from "../types/SelectedMaterial";
import {EstimateProductQty} from "../types/EstimateProductQty";
import {useParams} from "react-router-dom";
import {useGetEstimate} from "../api/get-estimate";
import {SaveEstimateRequest} from "../types/SaveEstimateRequest";
import {SavedData} from "../types/SavedData";
import {ValidationType} from "../../../../types";
import {CustomerInformation} from "../types/CustomerInformation";
import {useSmallJobs} from "../hooks/useSmallJobs";
import {Color} from "../types/Color";

const CountertopContext = createContext<CountertopsContextType>({} as CountertopsContextType);
const useCountertops = () => useContext(CountertopContext);

function CountertopProvider(props: React.PropsWithChildren<unknown>) {
    const {quoteId} = useParams();
    const [note, setNote] = useState<string>('');
    const estimateResponse = useGetEstimate(quoteId ?? '0');
    const [backsplashCounter, setBacksplashCounter] = useState<number>(1);
    const [showSinks, setShowSinks] = useState<boolean>(true);
    const [showSafetyCorners, setShowSafetyCorners] = useState<boolean>(true);
    const [showCutouts, setShowCutouts] = useState<boolean>(true);
    const [showFinishingTouches, setShowFinishingTouches] = useState<boolean>(true);
    const [counter, setCounter] = useState<number>(4);
    const [shapes, setShapes] = useState<Countertop[]>([]);
    const [selectedGroups, setSelectedGroups] = useState<SelectedMaterial[]>([]);
    const [estimateDate, setEstimateDate] = useState<Date>(new Date());
    const [expirationDate, setExpirationDate] = useState<Date>(new Date());
    const [dealerId, setDealerId] = useState<number>(0);
    const [oneQtyMap, setOneQtyMap] = useState<Map<number, number>>(new Map<number, number>());
    const [showPrices, setShowPrices] = useState<boolean>(false);
    // Customer information
    const [customerName, setCustomerName] = useState<string>('');
    const [customerAddress1, setCustomerAddress1] = useState<string>('');
    const [customerAddress2, setCustomerAddress2] = useState<string>('');
    const [customerZip, setCustomerZip] = useState<string>('');
    const [customerState, setCustomerState] = useState<string>('');
    const [customerCity, setCustomerCity] = useState<string>('');
    const [customerEmail, setCustomerEmail] = useState<string>('');
    const [customerPhone, setCustomerPhone] = useState<string>('');
    const [retailPriceBucketID, setRetailPriceBucketID] = useState<number | undefined>(undefined);
    
    const smallJobs = useSmallJobs(shapes, dealerId, customerZip);

    useEffect(() => {
        if (estimateResponse.data) {
            setDealerId(estimateResponse.data.dealerId);
            setEstimateDate(estimateResponse.data.quoteDate);
            setExpirationDate(estimateResponse.data.expirationDate);
            setCustomerZip(estimateResponse.data.customerZipCode);
            if (estimateResponse.data.drawingData) {
                loadEstimate(estimateResponse.data.drawingData);
            } else {
                setShapes([createShape(1), createShape(2), createShape(3)]);
            }
        } else {
            setShapes([createShape(1), createShape(2), createShape(3)]);
        }
    }, [estimateResponse.data])

    const qtyValidation = useCallback(
        ({value}: ValidationType<number | null>) => {
            if (value === null || value === 0)
                return false;

            return true;
        },
        []
    );

    const qtyValidationNotMandatory = useCallback(
        ({value}: ValidationType<number | null>) => {
            if (value === 0)
                return false;

            return true;
        },
        []
    );

    function addShape() {
        let newShape = createShape(counter);

        setShapes([...shapes, newShape]);
        setCounter(counter + 1);
    }

    function createShape(id: number): Countertop {
        return {
            id: id,
            name: 'Shape ' + id,
            width: undefined,
            length: undefined,
            total: undefined,
            prodQteGrpID: null,
            productID: null,
            backsplashes: [],
            materialName: '',
            colorName: '',
            smallProductFeeID: undefined,
        }
    }

    function removeShape(id: number) {
        let filteredArray = shapes.filter(item => item.id !== id);
        recalculateSelectedGroups(filteredArray);
        setShapes([...filteredArray]);
    }

    function updateShapeDimensions(id: number, length: number | undefined, width: number | undefined, total: number | undefined) {
        updateShape(id, (shape) => {
            return {...shape, length: length, width: width, total: total};
        });
    }

    function updateShapeName(id: number, name: string) {
        updateShape(id, (shape) => {
            return {...shape, name: name};
        });
    }

    function updateBacksplashDimensions(shapeId: number, id: number, length: number | undefined, height: number | undefined, total: number | undefined) {
        updateShape(shapeId, (shape) => {
            const backsplashes = shape.backsplashes.map(backsplash => {
                if (backsplash.id === id) {
                    return {...backsplash, length, height, total};
                } else {
                    return {...backsplash};
                }
            });
            return {...shape, backsplashes: backsplashes}
        });
    }

    function setShapeGroup(id: number, prodQteGrpID: number, materialName: string) {
        const changedArray = updateShapeManual(id, (shape) => {
            return {
                ...shape,
                prodQteGrpID: prodQteGrpID,
                productID: null,
                colorName: '',
                materialName: materialName,
                smallProductFeeID: undefined
            };
        });

        recalculateSelectedGroups(changedArray);
        setShapes(changedArray);
    }

    function recalculateSelectedGroups(arr: Countertop[]) {
        let materials = [] as SelectedMaterial[];

        for (let i = 0; i < arr.length; i++) {
            if (arr[i].prodQteGrpID === null)
                continue;

            if (!materials.some(x => x.prodQteGrpID === arr[i].prodQteGrpID)) {
                let existingItem = selectedGroups.find(item => item.prodQteGrpID === arr[i].prodQteGrpID);
                if (existingItem === undefined) {
                    materials.push({
                        prodQteGrpID: arr[i].prodQteGrpID as number,
                        name: arr[i].materialName,
                        edges: [],
                        sinks: [],
                        radii: [],
                        options: [],
                        cutouts: []
                    });
                } else {
                    materials.push({...existingItem});
                }
            }
        }

        setSelectedGroups(materials);
    }

    function updateEdge(prodQteGrpID: number, productID: number, length: number | undefined) {
        let existingItem = selectedGroups.find(item => item.prodQteGrpID === prodQteGrpID);
        if (existingItem === undefined) {
            return
        }

        let existingEdge = existingItem.edges.find(item => item.productID === productID);
        if (existingEdge === undefined) {
            updateGroup(prodQteGrpID, (group) => {
                return {...group, edges: [...group.edges, {productID, length}]};
            });
        } else {
            const newEdges = existingItem.edges.map(edge => {
                if (edge.productID === productID) {
                    return {productID, length};
                } else {
                    return {...edge};
                }
            });
            updateGroup(prodQteGrpID, (group) => {
                return {...group, edges: newEdges};
            });
        }
    }

    function removeSink(prodQteGrpID: number, productID: number) {
        let existingItem = selectedGroups.find(item => item.prodQteGrpID === prodQteGrpID);
        if (existingItem === undefined) {
            return
        }

        let existingSink = existingItem.sinks.find(item => item.productID === productID);
        let newSinks = existingItem.sinks.filter(item => item !== existingSink);

        updateGroup(prodQteGrpID, (group) => {
            return {...group, sinks: newSinks};
        });
    }

    function updateSink(prodQteGrpID: number, productID: number, qty: number | undefined, laborProductID: number | undefined) {
        let existingItem = selectedGroups.find(item => item.prodQteGrpID === prodQteGrpID);
        if (existingItem === undefined) {
            return
        }

        let existingSink = existingItem.sinks.find(item => item.productID === productID);
        if (existingSink === undefined) {
            updateGroup(prodQteGrpID, (group) => {
                return {...group, sinks: [...group.sinks, {productID, qty, laborProductID}]};
            });
        } else {
            const newSinks = existingItem.sinks.map(sink => {
                if (sink.productID === productID) {
                    return {productID, qty, laborProductID};
                } else {
                    return {...sink};
                }
            });
            updateGroup(prodQteGrpID, (group) => {
                return {...group, sinks: newSinks};
            });
        }
    }

    function removeRadius(prodQteGrpID: number, productID: number) {
        let existingItem = selectedGroups.find(item => item.prodQteGrpID === prodQteGrpID);
        if (existingItem === undefined) {
            return
        }

        let existingRadius = existingItem.radii.find(item => item.productID === productID);
        let newRadii = existingItem.radii.filter(item => item !== existingRadius);

        updateGroup(prodQteGrpID, (group) => {
            return {...group, radii: newRadii};
        });
    }

    function updateRadius(prodQteGrpID: number, productID: number, qty: number | undefined) {
        let existingItem = selectedGroups.find(item => item.prodQteGrpID === prodQteGrpID);
        if (existingItem === undefined) {
            return
        }

        let existingRadius = existingItem.radii.find(item => item.productID === productID);
        if (existingRadius === undefined) {
            updateGroup(prodQteGrpID, (group) => {
                return {...group, radii: [...group.radii, {productID, qty}]};
            });
        } else {
            const newRadii = existingItem.radii.map(radius => {
                if (radius.productID === productID) {
                    return {productID, qty};
                } else {
                    return {...radius};
                }
            });
            updateGroup(prodQteGrpID, (group) => {
                return {...group, radii: newRadii};
            });
        }
    }

    function addCustomOption(prodQteGrpID: number, productID: number) {
        let existingItem = selectedGroups.find(item => item.prodQteGrpID === prodQteGrpID);
        if (existingItem === undefined) {
            return
        }

        let existingOption = existingItem.options.find(item => item.productID === productID);
        if (existingOption === undefined) {
            updateGroup(prodQteGrpID, (group) => {
                return {
                    ...group,
                    options: [...group.options, {
                        productID,
                        qty: 0,
                        customOptions: [{description: '', id: 1, qty: 0}]
                    }]
                };
            });
        } else {
            const newOptions = existingItem.options.map(option => {
                if (option.productID === productID) {
                    const nextIndex = option.customOptions.reduce((max, x) => x.id > max ? x.id : max, 0) + 1;
                    return {
                        ...option,
                        customOptions: [...option.customOptions, {description: '', id: nextIndex, qty: 0}]
                    }

                } else {
                    return {...option};
                }
            });
            updateGroup(prodQteGrpID, (group) => {
                return {...group, options: newOptions};
            });
        }
    }

    function updateCustomOption(prodQteGrpID: number, productID: number, id: number, qty: number) {
        let existingItem = selectedGroups.find(item => item.prodQteGrpID === prodQteGrpID);
        if (existingItem === undefined) {
            return
        }

        const newOptions = existingItem.options.map(option => {
            if (option.productID === productID) {
                let newCustomOptions = option.customOptions.map(item => {
                    if (item.id === id) {
                        return {...item, qty: qty};
                    }
                    return {...item};
                });
                return {...option, customOptions: newCustomOptions};
            } else {
                return {...option};
            }
        });

        updateGroup(prodQteGrpID, (group) => {
            return {...group, options: newOptions};
        });
    }

    function updateCustomOptionDescription(prodQteGrpID: number, productID: number, id: number, description: string) {
        let existingItem = selectedGroups.find(item => item.prodQteGrpID === prodQteGrpID);
        if (existingItem === undefined) {
            return
        }

        const newOptions = existingItem.options.map(option => {
            if (option.productID === productID) {
                let newCustomOptions = option.customOptions.map(item => {
                    if (item.id === id) {
                        return {...item, description: description};
                    }
                    return {...item};
                });
                return {...option, customOptions: newCustomOptions};
            } else {
                return {...option};
            }
        });

        updateGroup(prodQteGrpID, (group) => {
            return {...group, options: newOptions};
        });
    }

    function removeCustomOption(prodQteGrpID: number, productID: number, id: number) {
        let existingItem = selectedGroups.find(item => item.prodQteGrpID === prodQteGrpID);
        if (existingItem === undefined) {
            return
        }

        let removeCurrentOption = true;
        const newOptions = existingItem.options.map(option => {
            if (option.productID === productID) {
                let newCustomOptions = option.customOptions.filter(item => item.id !== id);
                if (newCustomOptions.length > 0) {
                    removeCurrentOption = false;
                }
                return {...option, customOptions: newCustomOptions};
            } else {
                return {...option};
            }
        });
        if (removeCurrentOption) {
            removeOption(prodQteGrpID, productID);
        } else {
            updateGroup(prodQteGrpID, (group) => {
                return {...group, options: newOptions};
            });
        }
    }


    function removeOption(prodQteGrpID: number, productID: number) {
        let existingItem = selectedGroups.find(item => item.prodQteGrpID === prodQteGrpID);
        if (existingItem === undefined) {
            return
        }

        let existingOption = existingItem.options.find(item => item.productID === productID);
        let newOptions = existingItem.options.filter(item => item !== existingOption);

        updateGroup(prodQteGrpID, (group) => {
            return {...group, options: newOptions};
        });
    }

    function updateOption(prodQteGrpID: number, productID: number, qty: number | undefined) {
        let existingItem = selectedGroups.find(item => item.prodQteGrpID === prodQteGrpID);
        if (existingItem === undefined) {
            return
        }

        let existingOption = existingItem.options.find(item => item.productID === productID);
        if (existingOption === undefined) {
            updateGroup(prodQteGrpID, (group) => {
                return {...group, options: [...group.options, {productID, qty, customOptions: []}]};
            });
        } else {
            const newOptions = existingItem.options.map(option => {
                if (option.productID === productID) {
                    return {productID, qty, customOptions: []};
                } else {
                    return {...option};
                }
            });
            updateGroup(prodQteGrpID, (group) => {
                return {...group, options: newOptions};
            });
        }
    }

    function removeCutout(prodQteGrpID: number, productID: number) {
        let existingItem = selectedGroups.find(item => item.prodQteGrpID === prodQteGrpID);
        if (existingItem === undefined) {
            return
        }

        let existingCutout = existingItem.cutouts.find(item => item.productID === productID);
        let newCutouts = existingItem.cutouts.filter(item => item !== existingCutout);

        updateGroup(prodQteGrpID, (group) => {
            return {...group, cutouts: newCutouts};
        });
    }

    function updateCutout(prodQteGrpID: number, productID: number, qty: number | undefined) {
        let existingItem = selectedGroups.find(item => item.prodQteGrpID === prodQteGrpID);
        if (existingItem === undefined) {
            return
        }

        let existingCutout = existingItem.cutouts.find(item => item.productID === productID);
        if (existingCutout === undefined) {
            updateGroup(prodQteGrpID, (group) => {
                return {...group, cutouts: [...group.cutouts, {productID, qty}]};
            });
        } else {
            const newCutouts = existingItem.cutouts.map(cutout => {
                if (cutout.productID === productID) {
                    return {productID, qty};
                } else {
                    return {...cutout};
                }
            });
            updateGroup(prodQteGrpID, (group) => {
                return {...group, cutouts: newCutouts};
            });
        }
    }


    function setShapeProduct(id: number, productID: number, colorName: string) {
        setShapes((previousShapes) => {
            const selectedShape = previousShapes.find((item) => item.id === id);

            return previousShapes.map(shape => {
                if (shape.id === id) {
                    return {
                        ...shape,
                        productID: productID,
                        colorName: colorName
                    };
                } else if (shape.productID === null && selectedShape !== undefined) {
                    return {
                        ...shape,
                        productID: productID,
                        colorName: colorName,
                        prodQteGrpID: selectedShape.prodQteGrpID,
                        materialName: selectedShape.materialName
                    };
                } else {
                    return {...shape};
                }
            });
        });
    }

    function setShapeProductMetadata(id: number, selectedColor: Color) {
        setShapes((previousShapes) => {
            return previousShapes.map(shape => {
                if (shape.id === id) {
                    return {
                        ...shape,
                        smallProductFeeID: selectedColor?.smallProductFeeID
                    };
                } else {
                    return {...shape};
                }
            });
        });
    }

    function addBacksplash(shapeId: number) {
        updateShape(shapeId, (shape) => {
            let backsplashes = [...shape.backsplashes];
            backsplashes.push({
                id: backsplashCounter,
                name: shape.name + ' Backsplash',
                length: undefined,
                height: undefined,
                total: undefined
            });
            return {...shape, backsplashes: backsplashes}
        });
        setBacksplashCounter(backsplashCounter + 1);
    }

    function removeBacksplash(shapeId: number, id: number) {
        updateShape(shapeId, (shape) => {
            let backsplashes = shape.backsplashes.filter(item => item.id !== id);
            return {...shape, backsplashes: backsplashes}
        });
    }

    function updateShape(id: number, func: (shape: Countertop) => Countertop) {
        const resultArray: Countertop[] = shapes.map(shape => {
            if (shape.id === id) {
                return func(shape);
            } else {
                return {...shape};
            }
        });

        setShapes(resultArray);
    }

    function updateGroup(prodQteGrpID: number, func: (group: SelectedMaterial) => SelectedMaterial) {
        const resultArray: SelectedMaterial[] = selectedGroups.map(group => {
            if (group.prodQteGrpID === prodQteGrpID) {
                return func(group);
            } else {
                return {...group};
            }
        });

        setSelectedGroups(resultArray);
    }

    function updateGroups(func: (group: SelectedMaterial) => SelectedMaterial) {
        const resultArray: SelectedMaterial[] = selectedGroups.map(group => {
            return func(group);
        });

        setSelectedGroups(resultArray);
    }

    function updateShapeManual(id: number, func: (shape: Countertop) => Countertop) {
        const resultArray: Countertop[] = shapes.map(shape => {
            if (shape.id === id) {
                return func(shape);
            } else {
                return {...shape};
            }
        });

        return resultArray;
    }

    function getProductsToEstimate() {
        let result: EstimateProductQty[] = [];
        shapes.forEach(shape => {
            if (shape.productID !== null) {
                let qty = shape.total ?? 0;
                qty += shape.backsplashes.reduce((accumulator, currentValue) => accumulator + (currentValue.total ?? 0), 0);
                result.push({quantity: qty, productId: shape.productID, customDescription: undefined});
            }
        });

        selectedGroups.forEach(group => {
            group.edges.forEach(edge => {
                if (edge.length !== undefined) {
                    result.push({quantity: edge.length, productId: edge.productID, customDescription: undefined});
                }
            });
            group.sinks.forEach(sink => {
                if (sink.qty !== undefined) {
                    result.push({quantity: sink.qty, productId: sink.productID, customDescription: undefined});
                    if (sink.laborProductID !== undefined) {
                        result.push({quantity: sink.qty, productId: sink.laborProductID, customDescription: undefined});
                    }
                }
            });
            group.cutouts.forEach(cutout => {
                if (cutout.qty !== undefined) {
                    // logic for sink labor
                    let item = result.find(el => el.productId === cutout.productID);
                    if (item === undefined) {
                        result.push({quantity: cutout.qty, productId: cutout.productID, customDescription: undefined});
                    } else {
                        item.quantity = item.quantity + cutout.qty;
                    }
                }
            });
            group.radii.forEach(radius => {
                if (radius.qty !== undefined) {
                    result.push({quantity: radius.qty, productId: radius.productID, customDescription: undefined});
                }
            });
            group.options.forEach(option => {
                if (option.customOptions.length > 0) {
                    option.customOptions.forEach(customOption => {
                        if (customOption.qty !== undefined) {
                            result.push({
                                quantity: customOption.qty,
                                productId: option.productID,
                                customDescription: customOption.description
                            });
                        }
                    });
                } else {
                    if (option.qty !== undefined) {
                        result.push({quantity: option.qty, productId: option.productID, customDescription: undefined});
                    }
                }
            });
        });

        smallJobs.estimateItems.forEach((estimateItem) => {
            result.push({...estimateItem});
        });

        return result;
    }

    function updateOneQty(prodQteGrpID: number, productID: number | undefined) {
        oneQtyMap.get(prodQteGrpID);
        let newMap = new Map<number, number>(oneQtyMap);
        if (productID === undefined) {
            newMap.delete(prodQteGrpID);
        } else {
            newMap.set(prodQteGrpID, productID);
        }

        setOneQtyMap(newMap);
    }

    function getSaveEstimateRequest() {
        let savedData: SavedData = {
            backsplashCounter: backsplashCounter,
            counter: counter,
            shapes: shapes,
            selectedGroups: selectedGroups,
            note: note,
            showSinks: showSinks,
            showFinishingTouches: showFinishingTouches,
            showCutouts: showCutouts,
            showSafetyCorners: showSafetyCorners
        };

        function getCustomerInformation(): CustomerInformation | undefined {
            if (quoteId !== undefined)
                return undefined;

            return {
                name: customerName,
                address1: customerAddress1,
                address2: customerAddress2,
                zip: customerZip,
                state: customerState,
                city: customerCity,
                phone: customerPhone,
                email: customerEmail
            }
        }

        let result: SaveEstimateRequest = {
            quoteId: quoteId as any,
            prodQteGrpId: selectedGroups.length > 0 ? selectedGroups[0].prodQteGrpID : 0,
            estimateData: JSON.stringify(savedData),
            estimateItems: getProductsToEstimate(),
            note: note,
            saveAction: '',
            dealerID: dealerId,
            retailPriceBucketId: retailPriceBucketID,
            customerInformation: getCustomerInformation()
        };

        return result;
    }

    function loadEstimate(data: string) {
        let savedData: SavedData = JSON.parse(data);
        setBacksplashCounter(savedData.backsplashCounter);
        setCounter(savedData.counter);
        savedData.selectedGroups.forEach(x => {
            x.cutouts = x.cutouts ?? [];
        });
        setSelectedGroups(savedData.selectedGroups);
        setShapes(savedData.shapes);
        setNote(savedData.note);
        setShowSinks(savedData.showSinks ?? true);
        setShowSafetyCorners(savedData.showSafetyCorners ?? true);
        setShowCutouts(savedData.showCutouts ?? true);
        setShowFinishingTouches(savedData.showFinishingTouches ?? true);
    }

    function updateNote(text: string) {
        setNote(text);
    }

    function updateShowSinks(checked: boolean) {
        if (!checked) {
            updateGroups((group) => {
                return {...group, sinks: []};
            });
        }

        setShowSinks(checked);
    }

    function updateShowSafetyCorners(checked: boolean) {
        if (!checked) {
            updateGroups((group) => {
                return {...group, radii: []};
            });
        }

        setShowSafetyCorners(checked);
    }

    function updateShowCutouts(checked: boolean) {
        if (!checked) {
            updateGroups((group) => {
                return {...group, cutouts: []};
            });
        }

        setShowCutouts(checked);
    }

    function updateShowFinishingTouches(checked: boolean) {
        if (!checked) {
            updateGroups((group) => {
                return {...group, options: []};
            });
        }

        setShowFinishingTouches(checked);
    }

    function updateShowPrices(checked: boolean) {
        setShowPrices(checked);
    }

    return (
        <CountertopContext.Provider
            value={{
                shapes,
                estimateDate,
                expirationDate,
                dealerId,
                oneQtyMap,
                addShape,
                removeShape,
                updateShapeDimensions,
                updateShapeName,
                setShapeGroup,
                setShapeProduct,
                setShapeProductMetadata,
                addBacksplash,
                removeBacksplash,
                updateBacksplashDimensions,
                selectedGroups,
                updateEdge,
                updateSink,
                removeSink,
                updateRadius,
                removeRadius,
                updateOption,
                removeOption,
                updateCutout,
                removeCutout,
                addCustomOption,
                updateCustomOption,
                removeCustomOption,
                updateCustomOptionDescription,
                getProductsToEstimate,
                getSaveEstimateRequest,
                qtyValidation,
                qtyValidationNotMandatory,
                updateOneQty,
                note,
                updateNote,
                showSinks,
                updateShowSinks,
                showSafetyCorners,
                updateShowSafetyCorners,
                showCutouts,
                updateShowCutouts,
                showFinishingTouches,
                updateShowFinishingTouches,
                showPrices,
                updateShowPrices,
                setDealerId,
                isLoading: (quoteId !== undefined) && estimateResponse.isLoading,
                isNewEstimate: quoteId === undefined,
                customerName,
                setCustomerName,
                customerAddress1,
                setCustomerAddress1,
                customerAddress2,
                setCustomerAddress2,
                customerZip,
                setCustomerZip,
                customerState,
                setCustomerState,
                customerCity,
                setCustomerCity,
                customerEmail,
                setCustomerEmail,
                customerPhone,
                setCustomerPhone,
                retailPriceBucketID,
                setRetailPriceBucketID,
            }}
            {...props}
        />);

}

export {CountertopProvider, useCountertops}
